/**
 * Cross-user staleness, decided rather than inherited (review §6).
 *
 * Read-your-writes covers the actor: their own command's fragments land in
 * the cache synchronously. ANOTHER user's screen updates only on a refetch,
 * and at GridFit's scale that is fine — but only if the intervals are a
 * decision. This table is that decision.
 *
 * Keyed by query-name PREFIX so a family of queries inherits one policy, and
 * asserted total by `test/staleness.test.ts`: a query whose prefix matches
 * nothing takes the commercial default, and adding a family is a deliberate
 * edit here rather than a silent inheritance somewhere else.
 */
export interface StalenessPolicy {
  readonly staleTime: number
  readonly refetchOnWindowFocus: boolean
  readonly refetchInterval: number | false
}

const MINUTE = 60_000

/**
 * Exported (not merely `const`), so `test/staleness.test.ts` can assert the
 * actual MAPPING from a query family to its own decided policy, rather than
 * comparing `stalenessFor` against itself — which any total stub, including
 * one that ignores the query name entirely, would also satisfy.
 */
export const SITE_FACING: StalenessPolicy = {
  staleTime: 30_000,
  refetchOnWindowFocus: true,
  refetchInterval: false,
}

export const COMMERCIAL: StalenessPolicy = {
  staleTime: MINUTE,
  refetchOnWindowFocus: true,
  refetchInterval: false,
}

export const DASHBOARD: StalenessPolicy = {
  staleTime: 5 * MINUTE,
  refetchOnWindowFocus: true,
  refetchInterval: 5 * MINUTE,
}

export const REFERENCE: StalenessPolicy = {
  staleTime: 60 * MINUTE,
  refetchOnWindowFocus: false,
  refetchInterval: false,
}

/**
 * Longest prefix wins, so `dashboard.job` COULD differ from `dashboard` —
 * but nothing in this table today asks for that. Disclosed, not hidden:
 * measured (see `test/staleness.test.ts`'s doc comment) that no entry below
 * is a prefix of another, so `stalenessFor`'s longest-match tiebreak is
 * presently UNREACHABLE dead code. Kept anyway rather than simplified away:
 * it is cheap and correct, and the first family that genuinely needs a more
 * specific override than its parent (a `dashboard.job.` policy distinct from
 * plain `dashboard.`, say) gets it for free rather than this function
 * needing rewriting under time pressure. If that day comes, add a covering
 * test alongside the new entry.
 */
const BY_PREFIX: ReadonlyArray<readonly [string, StalenessPolicy]> = [
  ['site.', SITE_FACING],
  ['progress.', SITE_FACING],
  ['clock.', SITE_FACING],
  ['toy.', SITE_FACING],
  ['quote.', COMMERCIAL],
  ['job.', COMMERCIAL],
  ['dashboard.', DASHBOARD],
  ['config.', REFERENCE],
]

export const DEFAULT_POLICY = COMMERCIAL

export function stalenessFor(name: string): StalenessPolicy {
  let best: readonly [string, StalenessPolicy] | undefined
  for (const entry of BY_PREFIX) {
    if (name.startsWith(entry[0]) && (best === undefined || entry[0].length > best[0].length)) {
      best = entry
    }
  }
  return best?.[1] ?? DEFAULT_POLICY
}

export function stalenessPrefixes(): readonly string[] {
  return BY_PREFIX.map((entry) => entry[0])
}
